/**
 * Repositório Google Ads — invoca edge; nunca lê refresh_token.
 */
import { throwFunctionsInvokeError } from '../../lib/functionsInvoke';
import { getValidAccessToken, supabase } from '../../lib/supabase';
import { getGoogleAdsOAuthRedirectUri } from './googleAdsOAuth';

export type GoogleAdsCustomerOption = {
    customerId: string;
    name: string;
    loginCustomerId?: string;
    isManager?: boolean;
};
export type GoogleAdsConversionAction = { id: string; name: string; type: string };

export type GoogleAdsStatus = {
    connected: boolean;
    status?: string;
    customerName?: string;
    customerId?: string;
};

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
    // Garante JWT de usuário válido antes do invoke (evita 401 com sessão fantasma).
    await getValidAccessToken();
    const { data, error } = await supabase.functions.invoke('google-ads-api', { body });
    if (error || data?.error) {
        await throwFunctionsInvokeError(data, error, 'Falha na API Google Ads');
    }
    return data as T;
}

export async function exchangeGoogleAdsCode(
    code: string,
    organizationId: string
): Promise<{ integrationId: string; customers: GoogleAdsCustomerOption[] }> {
    return invoke({
        action: 'exchange-code',
        code,
        redirectUri: getGoogleAdsOAuthRedirectUri(),
        organizationId,
    });
}

export async function listGoogleAdsCustomers(
    organizationId: string
): Promise<GoogleAdsCustomerOption[]> {
    const data = await invoke<{ customers: GoogleAdsCustomerOption[] }>({
        action: 'list-customers',
        organizationId,
    });
    return data.customers || [];
}

export async function listGoogleAdsConversionActions(
    organizationId: string,
    customerId: string,
    loginCustomerId?: string
): Promise<GoogleAdsConversionAction[]> {
    const data = await invoke<{ actions: GoogleAdsConversionAction[]; error?: string }>({
        action: 'list-conversion-actions',
        organizationId,
        customerId,
        ...(loginCustomerId ? { loginCustomerId } : {}),
    });
    if (data.error) throw new Error(data.error);
    return data.actions || [];
}

export async function saveGoogleAdsIntegration(
    organizationId: string,
    input: {
        customerId: string;
        customerName: string;
        conversionActionIds: string[];
        loginCustomerId?: string;
    }
): Promise<void> {
    await invoke({
        action: 'save-integration',
        organizationId,
        ...input,
    });
}

export async function disconnectGoogleAds(organizationId: string): Promise<void> {
    await invoke({ action: 'disconnect', organizationId });
}

export async function getGoogleAdsStatus(organizationId: string): Promise<GoogleAdsStatus> {
    return invoke({ action: 'get-status', organizationId });
}

export async function uploadGoogleAdsConversion(
    organizationId: string,
    leadId: string,
    value: number,
    kind: 'lead' | 'purchase' = 'purchase'
): Promise<{ success: boolean; skipped?: boolean; error?: string }> {
    return invoke({
        action: 'upload-conversion',
        organizationId,
        leadId,
        value,
        kind,
    });
}
